import type { JsonObject } from '@bufbuild/protobuf';
import { fromJson } from '@bufbuild/protobuf';
import type {
  PlanningEntity,
  PlanningExecutionState,
  PlanningIterationDetails,
} from '@platform/timeline';
import {
  ExecutionCompletedSchema,
  ExecutionStartedSchema,
  PlanningCompletedSchema,
  PlanningIterationSchema,
  PlanningReflectionSchema,
  PlanningStartedSchema,
} from '../pb/proto/sem/middleware/planning_pb';
import { registerSem } from '../registry';

type PlanningAggregate = {
  runId: string;
  status: 'starting' | 'running' | 'completed' | 'error';
  createdAt: number;
  provider?: string;
  plannerModel?: string;
  maxIterations?: number;
  startedAtUnixMs?: number;
  totalIterations?: number;
  finalDecision?: string;
  statusReason?: string;
  finalDirective?: string;
  completedAtUnixMs?: number;
  iterations: Map<number, PlanningIterationDetails>;
  execution: PlanningExecutionState;
};

const aggregates = new Map<string, PlanningAggregate>();

function toNumber(value: unknown): number | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value === 'bigint') {
    const num = Number(value);
    return Number.isFinite(num) ? num : undefined;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

function cloneJson<T>(value: T | undefined): T | undefined {
  if (value === undefined) return undefined;
  try {
    return JSON.parse(JSON.stringify(value)) as T;
  } catch {
    return undefined;
  }
}

function ensureAggregate(
  runId: string,
  now: number
): { aggregate: PlanningAggregate; created: boolean } {
  const existing = aggregates.get(runId);
  if (existing) {
    return { aggregate: existing, created: false };
  }
  const aggregate: PlanningAggregate = {
    runId,
    status: 'starting',
    createdAt: now,
    iterations: new Map(),
    execution: { status: 'idle' },
  };
  aggregates.set(runId, aggregate);
  return { aggregate, created: true };
}

function toEntity(aggregate: PlanningAggregate, now: number): PlanningEntity {
  const iterations = Array.from(aggregate.iterations.values()).sort((a, b) => a.index - b.index);
  const execution =
    aggregate.execution?.status && aggregate.execution.status !== 'idle'
      ? aggregate.execution
      : undefined;
  return {
    id: aggregate.runId,
    kind: 'planning',
    timestamp: aggregate.startedAtUnixMs ?? aggregate.createdAt ?? now,
    props: {
      status: aggregate.status,
      runId: aggregate.runId,
      provider: aggregate.provider,
      plannerModel: aggregate.plannerModel,
      maxIterations: aggregate.maxIterations,
      startedAtUnixMs: aggregate.startedAtUnixMs,
      totalIterations: aggregate.totalIterations,
      finalDecision: aggregate.finalDecision,
      statusReason: aggregate.statusReason,
      finalDirective: aggregate.finalDirective,
      completedAtUnixMs: aggregate.completedAtUnixMs,
      iterations,
      execution,
    },
  };
}

registerSem('planning.start', ({ ev, now, convId }) => {
  const pb = fromJson(PlanningStartedSchema, (ev.data || {}) as JsonObject);
  const runId = pb.run?.runId || ev.id;
  if (!runId) return null;

  const { aggregate, created } = ensureAggregate(runId, now);
  aggregate.status = 'running';
  aggregate.provider = pb.run?.provider || aggregate.provider;
  aggregate.plannerModel = pb.run?.plannerModel || aggregate.plannerModel;
  aggregate.maxIterations = pb.run?.maxIterations || aggregate.maxIterations;
  const startedAt = toNumber(pb.startedAtUnixMs);
  aggregate.startedAtUnixMs = startedAt ?? aggregate.startedAtUnixMs ?? now;
  aggregate.createdAt = aggregate.startedAtUnixMs ?? aggregate.createdAt ?? now;

  return {
    kind: created ? 'add' : 'upsert',
    convId,
    entity: toEntity(aggregate, now),
  };
});

registerSem('planning.iteration', ({ ev, now, convId }) => {
  const pb = fromJson(PlanningIterationSchema, (ev.data || {}) as JsonObject);
  const runId = pb.run?.runId || ev.id;
  if (!runId) return null;

  const { aggregate, created } = ensureAggregate(runId, now);
  aggregate.status = 'running';
  aggregate.provider = pb.run?.provider || aggregate.provider;
  aggregate.plannerModel = pb.run?.plannerModel || aggregate.plannerModel;
  aggregate.maxIterations = pb.run?.maxIterations || aggregate.maxIterations;
  const emittedAt = toNumber(pb.emittedAtUnixMs);

  const index = pb.iterationIndex ?? 0;
  const existing = aggregate.iterations.get(index) ?? { index };
  const extra = cloneJson(pb.extra) as Record<string, unknown> | undefined;
  aggregate.iterations.set(index, {
    ...existing,
    index,
    action: pb.action || existing.action,
    reasoning: pb.reasoning || existing.reasoning,
    strategy: pb.strategy || existing.strategy,
    progress: pb.progress || existing.progress,
    toolName: pb.toolName || existing.toolName,
    reflectionText: pb.reflectionText || existing.reflectionText,
    emittedAtUnixMs: emittedAt ?? existing.emittedAtUnixMs,
    extra: extra ?? existing.extra,
  });

  return {
    kind: created ? 'add' : 'upsert',
    convId,
    entity: toEntity(aggregate, now),
  };
});

registerSem('planning.reflection', ({ ev, now, convId }) => {
  const pb = fromJson(PlanningReflectionSchema, (ev.data || {}) as JsonObject);
  const runId = pb.run?.runId || ev.id;
  if (!runId) return null;

  const { aggregate, created } = ensureAggregate(runId, now);
  aggregate.status = aggregate.status === 'starting' ? 'running' : aggregate.status;
  const index = pb.iterationIndex ?? 0;
  const existing = aggregate.iterations.get(index) ?? { index };
  const emittedAt = toNumber(pb.emittedAtUnixMs);
  aggregate.iterations.set(index, {
    ...existing,
    index,
    reflectionText: pb.reflectionText || existing.reflectionText,
    progressScore: pb.progressScore || existing.progressScore,
    emittedAtUnixMs: emittedAt ?? existing.emittedAtUnixMs,
  });

  return {
    kind: created ? 'add' : 'upsert',
    convId,
    entity: toEntity(aggregate, now),
  };
});

registerSem('planning.complete', ({ ev, now, convId }) => {
  const pb = fromJson(PlanningCompletedSchema, (ev.data || {}) as JsonObject);
  const runId = pb.run?.runId || ev.id;
  if (!runId) return null;

  const { aggregate, created } = ensureAggregate(runId, now);
  aggregate.provider = pb.run?.provider || aggregate.provider;
  aggregate.plannerModel = pb.run?.plannerModel || aggregate.plannerModel;
  aggregate.maxIterations = pb.run?.maxIterations || aggregate.maxIterations;
  aggregate.totalIterations = pb.totalIterations ?? aggregate.totalIterations;
  aggregate.finalDecision = pb.finalDecision || aggregate.finalDecision;
  aggregate.statusReason = pb.statusReason || aggregate.statusReason;
  aggregate.finalDirective = pb.finalDirective || aggregate.finalDirective;
  aggregate.completedAtUnixMs = toNumber(pb.completedAtUnixMs) ?? aggregate.completedAtUnixMs;

  const finalDecision = (aggregate.finalDecision || '').toLowerCase();
  const isError = finalDecision === 'error';
  aggregate.status = isError ? 'error' : 'completed';

  return {
    kind: created ? 'add' : 'upsert',
    convId,
    entity: toEntity(aggregate, now),
  };
});

registerSem('execution.start', ({ ev, now, convId }) => {
  const pb = fromJson(ExecutionStartedSchema, (ev.data || {}) as JsonObject);
  const runId = pb.runId || ev.id;
  if (!runId) return null;

  const { aggregate, created } = ensureAggregate(runId, now);
  aggregate.execution = {
    ...(aggregate.execution || { status: 'idle' }),
    status: 'running',
    executorModel: pb.executorModel || aggregate.execution?.executorModel,
    directive: pb.directive || aggregate.execution?.directive,
    startedAtUnixMs: toNumber(pb.startedAtUnixMs) ?? aggregate.execution?.startedAtUnixMs,
  };

  return {
    kind: created ? 'add' : 'upsert',
    convId,
    entity: toEntity(aggregate, now),
  };
});

registerSem('execution.complete', ({ ev, now, convId }) => {
  const pb = fromJson(ExecutionCompletedSchema, (ev.data || {}) as JsonObject);
  const runId = pb.runId || ev.id;
  if (!runId) return null;

  const { aggregate, created } = ensureAggregate(runId, now);
  const statusText = pb.status || aggregate.execution?.statusText;
  const completedStatus = (statusText || '').toLowerCase() === 'error' ? 'error' : 'completed';

  const extra = cloneJson(pb.extra) as Record<string, unknown> | undefined;
  aggregate.execution = {
    ...(aggregate.execution || { status: 'idle' }),
    status: completedStatus,
    statusText,
    completedAtUnixMs: toNumber(pb.completedAtUnixMs) ?? aggregate.execution?.completedAtUnixMs,
    errorMessage: pb.errorMessage || aggregate.execution?.errorMessage,
    tokensUsed: pb.tokensUsed || aggregate.execution?.tokensUsed,
    responseLength: pb.responseLength || aggregate.execution?.responseLength,
    extra: extra ?? aggregate.execution?.extra,
  };

  if (completedStatus === 'error') {
    aggregate.status = 'error';
  } else if (aggregate.status !== 'error') {
    aggregate.status = 'completed';
  }

  return {
    kind: created ? 'add' : 'upsert',
    convId,
    entity: toEntity(aggregate, now),
  };
});
